import React, { useEffect, useState } from 'react';
import Modal from '../Modal/Modal';
import sessionService from '../../services/session.service';
import './SessionWarningModal.css';

/**
 * Модальное окно предупреждения о скором истечении сессии
 *
 * Показывается за 5 минут до автоматического выхода.
 * Синхронизируется между всеми открытыми вкладками через BroadcastChannel.
 */
const SessionWarningModal: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [isExtending, setIsExtending] = useState(false);

  useEffect(() => {
    // Подписываемся на события предупреждения от session service
    const unsubscribe = sessionService.onWarning((seconds) => {
      if (seconds > 0) {
        setSecondsLeft(seconds);
        setIsOpen(true);
      } else {
        // Скрыть модалку (0 означает закрытие)
        setIsOpen(false);
        setSecondsLeft(0);
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    // Обратный отсчет времени
    if (!isOpen || secondsLeft <= 0) return;

    const timer = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          // Время истекло - закрываем модалку и выполняем logout
          setIsOpen(false);
          void sessionService.handleSessionExpired();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, [isOpen, secondsLeft]);

  /**
   * Форматирование времени в MM:SS
   */
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  /**
   * Обработчик кнопки "Остаться в системе"
   */
  const handleStayLoggedIn = async () => {
    setIsExtending(true);
    try {
      await sessionService.extendSession();
      setIsOpen(false);
    } catch (error) {
      console.error('Ошибка при продлении сессии:', error);
    } finally {
      setIsExtending(false);
    }
  };

  /**
   * Обработчик кнопки "Выйти"
   */
  const handleLogout = async () => {
    setIsOpen(false);
    await sessionService.logout();
  };

  // Не закрывать модалку по клику вне области или Escape
  const handleClose = () => {
    // Модалка закрывается только по кнопкам
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="⚠️ Предупреждение о сессии"
      size="md"
    >
      <div className="session-warning-modal">
        <div className="session-warning-content">
          <div className="session-warning-timer">
            <div className="session-warning-timer-label">Время до выхода:</div>
            <div className="session-warning-timer-value">{formatTime(secondsLeft)}</div>
          </div>

          <p className="session-warning-message">
            Ваша сессия скоро истечет из-за неактивности.
          </p>

          <p className="session-warning-hint">
            Нажмите &quot;Остаться в системе&quot; для продления сессии или &quot;Выйти&quot; для завершения работы.
          </p>
        </div>

        <div className="session-warning-actions">
          <button
            type="button"
            className="session-warning-btn session-warning-btn-primary"
            onClick={handleStayLoggedIn}
            disabled={isExtending}
          >
            {isExtending ? 'Продление...' : 'Остаться в системе'}
          </button>

          <button
            type="button"
            className="session-warning-btn session-warning-btn-secondary"
            onClick={handleLogout}
            disabled={isExtending}
          >
            Выйти
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default SessionWarningModal;
